import { durationLabel } from "@/lib/utils";
import React, { useEffect, useState } from "react";
import { goToCheckout } from "@/lib/checkout";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { formatMoney } from "@/lib/money";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sparkles, Globe } from "lucide-react";
import { FindoSpinner } from "@/components/FindoSpinner";

/** B2C ad boost tier picker → Stripe checkout redirect. Requires a logged-in B2C user. */
export default function BoostAdDialog({ adId, open, onClose, renew = false, country = "" }) {
  const { t, lang } = useLang();
  const [tiers, setTiers] = useState([]);
  const [region, setRegion] = useState(null);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) api(`/public-ads/boost/tiers${country ? `?country=${encodeURIComponent(country)}` : ""}`, { auth: false }).then((r) => { setTiers(r.tiers || []); setRegion(r.region || null); }).catch(() => {});
  }, [open, country]);

  async function pay() {
    if (!sel) return;
    setBusy(true);
    try {
      const res = await api(`/b2c/ads/${adId}/boost`, { method: "POST", b2c: true, body: { tier: sel, origin_url: window.location.origin } });
      if (res.url) { goToCheckout(res.url); return; }
    } catch (e) { toast.error(e.message); setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="boost-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Sparkles size={18} className="text-findo-red" /> {t("bo.title")}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-500">{t("bo.subtitle")}</p>
        {region === "afrique" && (
          <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold px-3 py-1 w-fit" data-testid="boost-africa-badge">
            <Globe size={13} /> {t("bo.africaPrice")}
          </div>
        )}
        <div className="grid grid-cols-2 gap-2 mt-2">
          {tiers.map((tier) => (
            <button
              key={tier.key}
              onClick={() => setSel(tier.key)}
              data-testid={`boost-tier-${tier.key}`}
              className={`rounded-xl border p-3 text-left transition-colors ${sel === tier.key ? "border-findo-red bg-findo-red/5 ring-1 ring-findo-red" : "border-border hover:border-findo-navy"}`}>
              <p className="font-display text-lg font-semibold text-findo-navy">{formatMoney(tier.price_eur, lang)}</p>
              <p className="text-xs text-slate-500">{tier.hours ? durationLabel(tier.hours, lang) : tier.label_fr}</p>
            </button>
          ))}
        </div>
        <Button onClick={pay} disabled={!sel || busy} className="w-full mt-3 rounded-xl font-semibold bg-findo-red hover:bg-findo-red/90" data-testid="boost-pay-btn">
          {busy ? <FindoSpinner size={16} /> : renew ? t("bo.renew") : t("bo.pay")}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
